import * as Sentry from "@sentry/react";

import { useErrorTexts, useProductTexts } from "Store/texts/hooks";
import { Form, FormikProps } from "formik";
import { BOAT_FIND_STEP, BOAT_PRICE_STEP } from "../boatSteps";

import { StyledAlert } from "App/global-style";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { AlertType } from "Components/Form/Alert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { NavigationButtons } from "Components/NavigationButtons";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { ReduxState } from "Store/rootReducer";
import { mapValidationErrors } from "Utils/formUtils";
import { isNull } from "Utils/functions";
import React from "react";
import { useSelector } from "react-redux";
import { BoatProductIntroFormValues } from "../ProductIntro/boatProductIntroInterfaces";
import { getDeductible } from "../boatUtils";
import { BoatCoverages } from "./BoatCoverages";
import {
  defaultCoverageForBoatType,
  defaultDeductibleForBoatType,
} from "./boatCoveragesUtils";
import { BoatProductPriceFormValues } from "./boatProductPriceInterfaces";

const productName = INSURANCE_NAME_VALUE.BOAT;

export const BoatProductPrice = (): React.ReactElement => {
  const handleAddProductToCart = useAddProductToCartHandler(BOAT_PRICE_STEP.id);
  const [texts, lang] = useProductTexts(productName.toLowerCase());
  const introFormValues = useFormState(
    BOAT_FIND_STEP.id,
  ) as Required<BoatProductIntroFormValues>;
  const formState = useFormState<BoatProductPriceFormValues>(
    BOAT_PRICE_STEP.id,
    {
      coverage: defaultCoverageForBoatType(introFormValues.boatType),
      deductible: defaultDeductibleForBoatType(introFormValues.boatType),
    },
  );
  const priceCalculation = useSelector(
    (state: ReduxState) => state.priceCalculation.priceCalculationResult,
  );
  const [{ callUsError, callUsAlertTitle }] = useErrorTexts();
  const { addProductToCart } = useSelector((state: ReduxState) => state);

  const handleSubmit = (formValues: BoatProductPriceFormValues): void => {
    const productToAdd = {
      coverage: formValues.coverage,
      productType: productName,
      filterValue: getDeductible(formValues),
    };

    handleAddProductToCart(formValues, [productToAdd]);
  };

  return (
    <Sentry.ErrorBoundary>
      <ProductPriceTile
        header={texts.tile.productPrice.heading[lang]}
        initialValues={formState}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<BoatProductPriceFormValues>) => (
          <Form>
            <BoatCoverages />

            {!isNull(addProductToCart.error) && (
              <StyledAlert
                type="error"
                title={callUsAlertTitle[lang]}
                description={callUsError[lang]}
                alertId="addProductToCart"
                alertType={AlertType.SYSTEM_ERROR_ALERT}
                marginTop="32px"
                fluid
              />
            )}

            <ErrorsSummedUp
              errors={mapValidationErrors(
                formProps.errors,
                texts.details,
                lang,
              )}
              isVisible
            />

            <NavigationButtons
              isUpsell
              isLoading={
                addProductToCart.isLoading || priceCalculation.isLoading
              }
            />
          </Form>
        )}
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
